import { all, call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    adhubConnectionOAuthExchangeUrl,
    adhubConnectionOAuthUrl,
    adhubConnectionPlatformUrl,
    adhubConnectionsUrl,
} from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import { httpRequest } from '../types';
import { adhubBrandHeaders } from '../../utils/adhubConnections';
import type {
    IAdHubConnectionsResponse,
    IAdHubOAuthExchangeResult,
    IAdHubOAuthUrlResponse,
} from '../../types/global/adhubConnections.types';
import { settle } from '../sandbox/sagaHelpers';
import {
    disconnectAdHubPlatform,
    disconnectAdHubPlatformFail,
    disconnectAdHubPlatformSuccess,
    exchangeAdHubMetaOAuth,
    exchangeAdHubMetaOAuthFail,
    exchangeAdHubMetaOAuthSuccess,
    fetchAdHubConnections,
    fetchAdHubConnectionsFail,
    fetchAdHubConnectionsSuccess,
    fetchAdHubMetaOAuthUrl,
    fetchAdHubMetaOAuthUrlFail,
    fetchAdHubMetaOAuthUrlSuccess,
} from './actions';

function unwrapData<T>(response: any): T {
    if (response?.data !== undefined) return response.data as T;
    return response as T;
}

function adhubApi(brandId: string, url: string, method: string, body: any = null) {
    return call(
        api,
        url,
        method,
        body,
        2,
        null,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        adhubBrandHeaders(brandId),
    );
}

function* fetchAdHubConnectionsSaga({ payload: brandId, resolve, reject }: any): Generator<any, void, any> {
    try {
        const response = yield adhubApi(brandId, adhubConnectionsUrl, httpRequest.GET);
        const data = unwrapData<IAdHubConnectionsResponse>(response);
        yield put(fetchAdHubConnectionsSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchAdHubConnectionsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchAdHubMetaOAuthUrlSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, redirectUri } = payload;
    try {
        const response = yield adhubApi(brandId, adhubConnectionOAuthUrl('meta', redirectUri), httpRequest.GET);
        const data = unwrapData<IAdHubOAuthUrlResponse>(response);
        if (!data?.url) throw new Error('AdHub did not return an OAuth URL');
        yield put(fetchAdHubMetaOAuthUrlSuccess(data.url));
        settle(resolve, reject, data.url);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchAdHubMetaOAuthUrlFail(error));
        settle(resolve, reject, error, true);
    }
}

function* exchangeAdHubMetaOAuthSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, body } = payload;
    try {
        const response = yield adhubApi(brandId, adhubConnectionOAuthExchangeUrl('meta'), httpRequest.POST, body);
        const data = unwrapData<IAdHubOAuthExchangeResult>(response);
        yield put(exchangeAdHubMetaOAuthSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(exchangeAdHubMetaOAuthFail(error));
        settle(resolve, reject, error, true);
    }
}

function* disconnectAdHubPlatformSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, platform } = payload;
    try {
        yield adhubApi(brandId, adhubConnectionPlatformUrl(platform), httpRequest.DELETE, {});
        yield put(disconnectAdHubPlatformSuccess());
        settle(resolve, reject, undefined);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(disconnectAdHubPlatformFail(error));
        settle(resolve, reject, error, true);
    }
}

export default function* adhubSaga() {
    yield all([
        takeLatest(fetchAdHubConnections as any, fetchAdHubConnectionsSaga),
        takeLatest(fetchAdHubMetaOAuthUrl as any, fetchAdHubMetaOAuthUrlSaga),
        takeLatest(exchangeAdHubMetaOAuth as any, exchangeAdHubMetaOAuthSaga),
        takeLatest(disconnectAdHubPlatform as any, disconnectAdHubPlatformSaga),
    ]);
}
